import { BaseEdge, EdgeLabelRenderer, getBezierPath, getSmoothStepPath, type Edge, type EdgeProps } from '@xyflow/react'
import { offsetEdgeLabel } from '@/frontend/features/Fde/graphEdgeLabelPlacement'
import { processEdgeLabelDimensions } from '@/frontend/features/Fde/layoutProcessFlow'
import type { ProcessTransitionKind } from '@/frontend/features/Fde/types'

interface ProcessFlowEdgeData extends Record<string, unknown> {
  transitionKind: ProcessTransitionKind
}

export type ProcessCanvasEdge = Edge<ProcessFlowEdgeData, 'process'>

export default function ProcessFlowEdge(props: EdgeProps<ProcessCanvasEdge>) {
  const transitionKind = props.data?.transitionKind ?? 'sequence'
  const curved = ['return', 'retry'].includes(transitionKind)
  const branch = ['exception', 'cancel'].includes(transitionKind)
  const [path, labelX, labelY] = curved ? getBezierPath(props) : getSmoothStepPath({
    ...props,
    borderRadius: 10,
    offset: 22,
    stepPosition: branch ? 0.2 : 0.5,
  })
  const label = typeof props.label === 'string' ? props.label.trim() : ''
  const labelSize = processEdgeLabelDimensions(label)
  const labelPosition = offsetEdgeLabel(
    { x: labelX, y: labelY },
    { x: props.targetX - props.sourceX, y: props.targetY - props.sourceY },
    labelSize,
  )

  return <>
    <BaseEdge id={props.id} path={path} markerEnd={props.markerEnd} style={props.style} interactionWidth={20} />
    {label && <EdgeLabelRenderer><div
      className="nodrag nopan pointer-events-none absolute z-30 flex items-center justify-center px-1"
      style={{ width: labelSize.width, height: labelSize.height, transform: `translate(-50%, -50%) translate(${labelPosition.x}px, ${labelPosition.y}px)` }}
      data-testid="process-flow-edge-label"
    >
        <span className="line-clamp-3 min-w-0 max-w-full whitespace-normal break-words rounded-md border border-border/70 bg-background/95 px-2 py-1 text-center text-[10px] font-semibold leading-3.5 text-foreground shadow-sm">
          {label}
        </span>
    </div></EdgeLabelRenderer>}
  </>
}
